import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import {
	NavObjectType,
	NavRoute,
	type Account,
	type BadgeInfo,
	type NavigationEntryPointProps, // full prop shape
	type NavigationImperativeApi,
	type NavTarget,
	type ProfileStatus,
	type RecentItem,
} from '@databox/ember-integrations/lib/components/Navigation/navigation.contract';
import NavigationEntryPoint from '@databox/ember-integrations/lib/components/Navigation/NavigationEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { equal, sortBy } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

import type { ActivityLogObject } from 'webapp/models-custom/activity-log';
import Report from 'webapp/models-custom/report';

interface Args {
	onIsExpandedChange: (isExpanded: boolean) => void;
}

export default class NavigationMainComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('react') declare reactService: Service.React;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('account-progress') declare accountProgressService: Service.AccountProgress;
	@service('navigation-main') declare navigationMainService: Service.NavigationMain;
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('socket') declare socketService: Service.Socket;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('tour') declare tourService: Service.Tour;

	reactElement: ReactIntegrations.Root = null;

	navigationImperativeApi: NavigationImperativeApi = {
		refreshFavorites: () => {
			/* nothing */
		},
		getAiThreadsSummary: () => Promise.resolve(null),
	};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// The server-rendered page sets it; without one, start empty and fill what's needed here.
		if (!window._env_) {
			window._env_ = {} as typeof window._env_;
		}

		if (!window._env_.ANALYTICS_API_URL) {
			window._env_.ANALYTICS_API_URL = this.requestService.apiUrl ?? '';
		}

		// need these for recents (not only when on BI route, even on hover)
		void Promise.allSettled([
			this.mmBoardLoopsService.requestBoardLoops(),
			this.mmReportsService.requestReports(),
		]).then(() => {
			this.fetchRecentItemsData();
		});
	}

	get isDarkThemeActive(): boolean {
		return this.navigationService.theme.isDark;
	}

	get activeRoute() {
		return this.navigationMainService.activatedNavRoute;
	}

	// The open chat thread, so the AI panel marks that row as selected. Derived in navigation-main next to the
	// activatedNavRoute it is gated on; see that getter for the reasoning.
	get activeThreadId(): string | null {
		return this.navigationMainService.activeThreadId;
	}

	// The open routine, so the AI panel marks that Routines row as selected. Derived in navigation-main next
	// to the activatedNavRoute it is gated on; see that getter for the reasoning.
	get activeRoutineId(): string | null {
		return this.navigationMainService.activeRoutineId;
	}

	get user(): NavigationEntryPointProps['user'] {
		const model = this.currentUserService.model!;

		return {
			id: model.numericIdForSure,
			name: model.name,
			email: model.email,
			avatar: model.avatar,
		};
	}

	@cached
	get accounts(): Account[] {
		const accounts: Account[] = [];

		const stripColorHash = (color?: string) => {
			return (color || '').substr(1) || null;
		};

		if (this.mmSpacesService.isMultipleSpaces) {
			const spaces = [
				this.mmSpacesService.agencySpaceModel!,
				...sortBy(this.mmSpacesService.otherSpaceModels, 'companyName'),
			];
			spaces.forEach((space) => {
				accounts.push({
					id: space.numericId!,
					name: space.companyName,
					color: stripColorHash(space.color),
					isAgency: space.isEnterprise,
					isClient: !space.isEnterprise,
				});
			});
		} else {
			accounts.push({
				id: this.accountService.id,
				name: this.accountService.companyName,
				color: stripColorHash(this.accountService.currentSpace?.color),
				isAgency: this.accountService.isAgency,
				isClient: this.accountService.isClient,
			});
		}

		return accounts;
	}

	get profileStatus(): ProfileStatus {
		const activeAccountProgressStage = this.accountProgressService.activeStage?.id;

		return {
			progress: {
				id: activeAccountProgressStage as 1 | 2 | 3,
				percentage: this.accountProgressService.pointsWithPercentage,
			},
			hasUnseenUpdates: this.currentUserService.hasNotSeenNewProductUpdates,
		};
	}

	get preferExpanded(): boolean {
		return this.userSettingsService.preferMainNavigationExpanded;
	}

	get hideNavRoutes(): NavRoute[] {
		return this.navigationMainService.hideNavRoutes;
	}

	onNavigate = (target: NavTarget): void => {
		this.navigationMainService.handleNavTarget(target);
	};

	onPreferExpandedChange = (preferExpanded: boolean): void => {
		this.userSettingsService.preferMainNavigationExpanded = preferExpanded;
	};

	@action
	handleLogoClick(e: MouseEvent): void {
		e.preventDefault();

		if (!this.currentUserService.isOnboarded) {
			if (e.detail === 5) {
				// 5 clicks
				this.onboardingService.markCompleted();
			}
			return;
		}

		this.navigationService.transitionToRoute(
			this.currentUserService.isViewer ? 'boards' : 'index',
		);
	}

	// ---- Favorite items refresh START ----

	private _favoriteItemsCache = 0;

	@cached
	get favoriteItemsCache() {
		// also caching name and color bcos react needs to refetch on that
		const getFavoriteInfo = (
			list: {
				favorite: boolean;
				numericId: number | null;
				color: string;
				name?: string;
				title?: string;
			}[],
		) => {
			return list
				.filter((item) => !!item.numericId && item.favorite)
				.map((item) => {
					return {
						id: item.numericId!,
						color: item.color,
						title: item.name || item.title,
					};
				});
		};
		const favBoards = getFavoriteInfo(this.mmBoardsService.boardModels);
		const favLoops = getFavoriteInfo(this.mmBoardLoopsService.boardLoopModels);
		const favReports = getFavoriteInfo(this.mmReportsService.reportModels);
		const favItems = [
			...favBoards.map((data) => {
				return { type: NavObjectType.BOARD, ...data };
			}),
			...favLoops.map((data) => {
				return { type: NavObjectType.BOARD_LOOP, ...data };
			}),
			...favReports.map((data) => {
				return { type: NavObjectType.REPORT, ...data };
			}),
		];
		const cache = hashCode(JSON.stringify(sortBy(favItems, 'type', 'id')));
		return cache;
	}

	@action
	checkToUpdateFavoriteItemsCache() {
		const oldCache = this._favoriteItemsCache;
		const newCache = this.favoriteItemsCache;
		if (oldCache !== newCache) {
			this._favoriteItemsCache = newCache;
			// navigation will refetch favorites - give the request time to process
			debounce(this, this.dispatchImperativeRefreshFavorites, 1000);
		}
	}

	@action
	dispatchImperativeRefreshFavorites() {
		this.navigationImperativeApi.refreshFavorites();
	}

	// ---- Favorite items refresh END ----

	// ---- Recents items START ----

	@tracked private _recentActivityLogData: ActivityLogObject[] = [];

	fetchRecentItemsData() {
		void this.mmActivityLogsService.getRecentlyViewedItems().then((reply) => {
			if (reply) {
				this._recentActivityLogData = reply.activityLogs;
			}
		});
	}

	private _recentItems: RecentItem[] = [];

	@cached
	get recentItems(): RecentItem[] {
		const oldRecents = this._recentItems;
		const newRecents: RecentItem[] = [];
		this.mmActivityLogsService
			.createModelsFromRecordsArray(this._recentActivityLogData)
			.forEach((model) => {
				const object = model.object;
				if (object) {
					const item: RecentItem = {
						name: object instanceof Report ? object.title : object.name,
						objectId: +model.objectId,
						objectType:
							this.navigationMainService.getNavObjectTypeFromActivityLogObjectType(
								model.objectType,
							)!,
						color: object.color,
					};
					newRecents.push(item);
				}
			});
		if (!equal(oldRecents, newRecents)) {
			next(() => {
				// eslint-disable-next-line ember/no-side-effects
				this._recentItems = newRecents;
				this.updateReactComponent();
			});
		}

		return newRecents;
	}

	get recentItemsCache() {
		return hashCode(JSON.stringify(this.recentItems));
	}

	@action
	handleRecentItemsViewedChanged() {
		debounce(this, this.fetchRecentItemsData, 2000);
	}

	// ---- Recents items END ----

	// ---- Badges START ----

	private _badgesCache = hashCode(JSON.stringify({}));

	@cached
	get badgesCache() {
		const badges = this.getBadges();
		const newCache = hashCode(JSON.stringify(badges));

		return newCache;
	}

	@action
	checkToUpdateBadgesCache() {
		const oldCache = this._badgesCache;
		const newCache = this.badgesCache;
		if (oldCache !== newCache) {
			this._badgesCache = newCache;
			this.updateReactComponent();
		}
	}

	private getBadges() {
		const badges: Partial<Record<NavRoute, BadgeInfo>> = {};
		const isCurrentUserViewer = this.currentUserService.isViewer;
		const {
			navigationMainDataboardsNotificationsCount,
			navigationMainDataboardsNotificationsCountTooltip,
			navigationMainReportsNotificationsCount,
			navigationMainSourcesNotificationsCount,
		} = this.appNotificationsService;

		{
			// databoards
			const count = isCurrentUserViewer ? 0 : navigationMainDataboardsNotificationsCount;
			if (count) {
				badges[NavRoute.BI_DATABOARDS] = {
					count,
					tooltip: navigationMainDataboardsNotificationsCountTooltip,
				};
			}
		}

		{
			// reports
			const count = isCurrentUserViewer ? 0 : navigationMainReportsNotificationsCount;
			if (count) {
				badges[NavRoute.BI_REPORTS] = {
					count,
					tooltip: `${count} Template${count === 1 ? '' : 's'} waiting to be used`,
				};
			}
		}

		{
			// sources
			const count = navigationMainSourcesNotificationsCount;
			if (count) {
				badges[NavRoute.DATA_SOURCES] = {
					count,
					tooltip: `${count} Source${count === 1 ? '' : 's'}  with issues`,
				};
			}
		}

		return badges;
	}

	// ---- Badges END ----

	// ---- React render ----

	@action
	setupView(node: HTMLElement): void {
		this.reactElement = this.reactService.create(node as HTMLDivElement);
		this.renderNavigation();

		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.updateReactComponent);
		this.eventManagerService.on(
			AppEvent.ACCESS_TOKEN_REFRESHED,
			this,
			this.updateReactComponent,
		);
		this.eventManagerService.on(
			AppEvent.RECENT_ITEMS_VIEWED_CHANGED,
			this,
			this.handleRecentItemsViewedChanged,
		);
	}

	@action
	onReactInit(navigationImperativeApi: NavigationImperativeApi) {
		this.navigationImperativeApi = navigationImperativeApi;
		this.navigationMainService.navigationImperativeApi = navigationImperativeApi;
	}

	renderNavigation(): void {
		if (!this.reactElement) {
			return;
		}

		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props: NavigationEntryPointProps = {
			isDarkThemeActive: this.isDarkThemeActive,
			activeRoute: this.activeRoute,
			activeThreadId: this.activeThreadId,
			activeRoutineId: this.activeRoutineId,
			hideNavRoutes: this.hideNavRoutes,
			preferExpanded: this.preferExpanded,
			user: this.user,
			accessToken: accessToken!,
			dataToken: dataToken!,
			logo: this.accountService.shownAppLogo,
			currentAccountId: this.accountService.id,
			accounts: this.accounts,
			profileStatus: this.profileStatus,
			biRecents: this.recentItems,
			badges: this.getBadges(),
			callbacks: {
				onInit: this.onReactInit,
				onNavigate: this.onNavigate,
				onPreferExpandedChange: this.onPreferExpandedChange,
				onIsExpandedChange: this.args.onIsExpandedChange,
				onLogoClick: this.handleLogoClick,
			},
			// Same bound listener the Genie page/panel receive - lets the sidebar react to
			// run.completed without any Genie screen open. The connection itself is established at
			// app boot; this only registers the React listener on it.
			subscribeToAiEvents: this.socketService.subscribeToAiEvents,
			tour: this.tourService.getGenieTourConfig(),
			onGenieAnswerReady: this.aiChatService.onGenieAnswerReady,
		};

		this.reactService.render(this.reactElement, NavigationEntryPoint(props));
	}

	@action
	updateReactComponent(): void {
		this.renderNavigation();
	}

	@action
	teardownView(): void {
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
		this.eventManagerService.off(AppEvent.RECENT_ITEMS_VIEWED_CHANGED, this);
		this.reactService.unmount(this.reactElement);
	}

	<template>
		<div
			id="main-navigation-portal"
			data-favcache={{this.favoriteItemsCache}}
			data-badgescache={{this.badgesCache}}
			data-recentcache={{this.recentItemsCache}}
			{{didInsert this.setupView}}
			{{didUpdate
				this.updateReactComponent
				this.activeRoute
				this.activeThreadId
				this.activeRoutineId
				this.isDarkThemeActive
				this.preferExpanded
				this.hideNavRoutes
				this.accounts
				this.profileStatus
				this.user
			}}
			{{didUpdate this.checkToUpdateFavoriteItemsCache this.favoriteItemsCache}}
			{{didUpdate this.checkToUpdateBadgesCache this.badgesCache}}
			{{willDestroy this.teardownView}}
		/>
	</template>
}
